// Centralized auth/session storage. Kept tiny and synchronous so interceptors
// and the auth context share one source of truth.

import type { AuthResult } from '@/types/auth';

/**
 * The company a public link belongs to. New links say "?company=CODE" — the same words the sign-in
 * screen uses ("Company code") — and "?tenant=" is the old spelling, still read so links already in
 * people's inboxes keep working.
 */
export function companyCodeFromUrl(params: { get(key: string): string | null }): string | null {
  return params.get('company') ?? params.get('tenant');
}

const ACCESS = 'st.accessToken';
const REFRESH = 'st.refreshToken';
const TENANT = 'st.tenantCode';
const ACCESS_EXP = 'st.accessTokenExpiresAt';
const BRANCH = 'st.branchId';
const REMEMBER = 'st.rememberMe';
/** When this tab's session began (sign-in, refresh or hand-over from another tab), in ms. */
const SINCE = 'st.sessionSince';

/**
 * Where the session lives, and why there are two places.
 *
 * <p>
 * With "Remember me" the session goes in <code>localStorage</code> and survives closing the browser
 * — the right default on somebody's own machine. Without it the session goes in
 * <code>sessionStorage</code>, which the browser drops the moment the tab closes. That is the whole
 * feature: on a shared or office computer, walking away and closing the tab ends the session
 * instead of leaving it signed in for the next person.
 * </p>
 * <p>
 * The choice itself is remembered in <code>localStorage</code> so the login screen can tick the box
 * the way it was left. It is a preference, not a credential.
 * </p>
 */
function remembered(): boolean {
  try { return localStorage.getItem(REMEMBER) === '1'; } catch { return true; }
}

/** The store this session is being kept in. */
function store(): Storage {
  return remembered() ? localStorage : sessionStorage;
}

/**
 * Reads look in BOTH stores, writes go to the chosen one.
 *
 * <p>
 * Reading from both matters on the hop between them: a session started without "Remember me" and
 * one started with it must both be found by an interceptor that has no idea which was used, and a
 * value left behind by a previous choice must never shadow the current session.
 * </p>
 */
function read(key: string): string | null {
  try {
    return store().getItem(key) ?? localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    store().setItem(key, value);
    // Never leave the same key behind in the other store; a stale copy would outlive the sign-out
    // that was supposed to clear it.
    (remembered() ? sessionStorage : localStorage).removeItem(key);
  } catch { /* private mode, quota — never worth breaking sign-in over */ }
}

function drop(key: string) {
  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch { /* ignore */ }
}

/** A session as it is handed between tabs of this browser. */
export interface SharedSession {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  tenantCode: string | null;
  branchId: number | null;
}

/** Told whenever THIS tab writes a new session (login, refresh), so other tabs can follow. */
let sessionWritten: (() => void) | null = null;

export const storage = {
  get accessToken() {
    return read(ACCESS);
  },
  get refreshToken() {
    return read(REFRESH);
  },
  get tenantCode() {
    return read(TENANT);
  },
  get accessTokenExpiresAt() {
    return read(ACCESS_EXP);
  },
  /** Whether this session was asked to survive the browser closing. */
  get rememberMe() {
    return remembered();
  },
  /**
   * Sets the preference. Called BEFORE the session is written, because it decides where it goes.
   */
  setRememberMe(value: boolean) {
    try {
      if (value) localStorage.setItem(REMEMBER, '1');
      else localStorage.removeItem(REMEMBER);
    } catch { /* ignore */ }
  },
  /**
   * Last selected branch id (a hint only). It is always re-validated against the branches the
   * backend says the user may access before use — never trusted as an access grant.
   */
  get branchId(): number | null {
    const raw = read(BRANCH);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  },
  setBranchId(id: number | null | undefined) {
    if (id && id > 0) write(BRANCH, String(id));
    else drop(BRANCH);
  },
  setTenant(code: string | null | undefined) {
    if (code) write(TENANT, code);
    else drop(TENANT);
  },
  setSession(auth: AuthResult) {
    write(ACCESS, auth.accessToken);
    write(REFRESH, auth.refreshToken);
    write(ACCESS_EXP, auth.accessTokenExpiresAt);
    write(SINCE, String(Date.now()));
    sessionWritten?.();
  },
  /** When this tab's session began, in ms — compared against a sign-out recorded by another tab. */
  get sessionSince(): number | null {
    const raw = read(SINCE);
    const value = raw ? Number(raw) : NaN;
    return Number.isFinite(value) ? value : null;
  },
  /** Registers the one listener for this tab's own session writes (the cross-tab sync). */
  onSessionWritten(listener: (() => void) | null) {
    sessionWritten = listener;
  },
  /** This tab's session, whole, or null when signed out. */
  get shared(): SharedSession | null {
    const accessToken = read(ACCESS);
    const refreshToken = read(REFRESH);
    if (!accessToken || !refreshToken) return null;
    return {
      accessToken,
      refreshToken,
      accessTokenExpiresAt: read(ACCESS_EXP) ?? '',
      tenantCode: read(TENANT),
      branchId: this.branchId,
    };
  },
  /**
   * Writes a session another tab handed over. Silent: it is not announced back, or two tabs would
   * echo the same session to each other forever.
   */
  adoptSession(session: SharedSession) {
    write(ACCESS, session.accessToken);
    write(REFRESH, session.refreshToken);
    write(ACCESS_EXP, session.accessTokenExpiresAt);
    write(SINCE, String(Date.now()));
    if (session.tenantCode) write(TENANT, session.tenantCode);
    if (session.branchId && session.branchId > 0) write(BRANCH, String(session.branchId));
  },
  clearSession() {
    drop(ACCESS);
    drop(REFRESH);
    drop(ACCESS_EXP);
    drop(BRANCH);
    drop(SINCE);
  },
  clearAll() {
    this.clearSession();
    drop(TENANT);
  },
};
